import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

export function ILPreferences() {
  const [ilEnabled, setIlEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPreference() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('profiles')
        .select('il_tracking_enabled')
        .eq('id', user.id)
        .single();

      if (data) setIlEnabled(data.il_tracking_enabled);
      setLoading(false);
    }
    loadPreference();
  }, []);

  const handleToggle = async (enabled: boolean) => {
    setIlEnabled(enabled);
    setSaving(true);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('Not authenticated');
      setSaving(false);
      return;
    }

    const { error } = await supabase
      .from('profiles')
      .update({ il_tracking_enabled: enabled })
      .eq('id', user.id);

    setSaving(false);

    if (error) {
      toast.error('Unable to save IL preference');
      console.error('Error saving IL preference:', error);
    } else {
      toast.success(enabled ? 'IL tracking enabled' : 'IL tracking disabled');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Inflammatory Load Tracking</CardTitle>
        <CardDescription>
          Control whether your Inflammatory Load score is computed and displayed
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="il-tracking" className="text-base">
              Enable IL Score Computation
            </Label>
            <p className="text-sm text-muted-foreground">
              Calculate daily inflammation indicators from your lifestyle data
            </p>
          </div>
          <Switch
            id="il-tracking"
            checked={ilEnabled}
            onCheckedChange={handleToggle}
            disabled={loading || saving}
          />
        </div>

        {!ilEnabled && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              IL tracking is disabled. Your IL score will not be computed or displayed. 
              You can re-enable it anytime from this page.
            </AlertDescription>
          </Alert>
        )}

        <div className="text-xs text-muted-foreground pt-2 border-t">
          <strong>What happens when disabled:</strong>
          <ul className="list-disc pl-4 mt-2 space-y-1">
            <li>Your IL score will not be calculated</li>
            <li>IL cards and dashboards will be hidden</li>
            <li>Historical IL data is preserved but not updated</li>
            <li>You can re-enable tracking anytime</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
